import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  CalendarEventId,
  CalendarEventRequest,
  CalendarEventResponse,
  CalendarEventUserId,
  CreateCalendarEventResponse,
  MoveCalendarEventRequest
} from '@business/entities/calendar_event';
import { CalendarEventAPI } from '@business/api/calendar_events';
import { CalendarImportRequest } from "@business/entities/calendar_template";
import { CalendarTemplateAPI } from "@business/api/calendar_template";

export class EventBloc extends BaseBloc {
  private readonly eventsRepo = new Repository<CalendarEventResponse>({
    getItemId: event => `${event.group}-${event.id}`,
  });

  readonly events$ = this.eventsRepo.items$;

  onReset() {
    this.eventsRepo.reset();
  }

  // Remembered so "update all" can refresh the visible range after the server shifts subsequent events.
  private lastFetchRequest: CalendarEventRequest | null = null;

  /** Re-run the most recent fetch (e.g. after another bloc changed events server-side). No-op before the first fetch. */
  refetchLastRange = () => (this.lastFetchRequest ? this.fetchEvents(this.lastFetchRequest) : of([] as CalendarEventResponse[]));

  fetchEvents = (request: CalendarEventRequest) => {
    this.lastFetchRequest = request;
    return CalendarEventAPI.fetchEvents(request).pipe(
      //   this.eventsRepo.ops.upsertMany(item => ({ item })),
      this.eventsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_companies_error') }),
    );
  };

  createEvent = (event: Partial<CreateCalendarEventResponse>) => {
    return CalendarEventAPI.createEvent(event).pipe(
      this.eventsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_event'),
      }),
    );
  };

  updateEvent = (id: string, event: Partial<CreateCalendarEventResponse> & { updateAll?: number }) => {
    return CalendarEventAPI.updateEvent(id, event).pipe(
      this.eventsRepo.ops.upsertOne(item => ({ item })),
      // "Update all" shifts the subsequent events server-side; the response only carries the edited
      // event, so refetch the visible range — otherwise the calendar shows stale dates until reload.
      switchMap(item => {
        if (event.updateAll && this.lastFetchRequest) {
          return this.fetchEvents(this.lastFetchRequest).pipe(map(() => item));
        }
        return of(item);
      }),
      handleMessage({
        type: requestMessage('update_event'),
      }),
    );
  };

  // Drag & drop / resize: only the dates change. Same "Update All" cascade as updateEvent.
  moveEvent = (id: CalendarEventId, request: MoveCalendarEventRequest) => {
    return CalendarEventAPI.moveEvent(id, request).pipe(
      this.eventsRepo.ops.upsertOne(item => ({ item })),
      // Always resync the visible range: the stored dates can differ from the drop (weekend/holiday
      // snap, Update All cascade) and the calendar must not keep the dragged copy on the old date.
      switchMap(item => (this.lastFetchRequest ? this.fetchEvents(this.lastFetchRequest).pipe(map(() => item)) : of(item))),
      handleMessage({
        type: requestMessage('move_event'),
      }),
    );
  };

  deleteEvent = (eventId: CalendarEventId) => {
    return showDeleteConfirmation('Delete Event', 'Do you really want to remove this event?').pipe(
      switchMap(() => CalendarEventAPI.deleteEvent(eventId)),
      tap(() => {
        const events = this.eventsRepo.getAllItems() as CalendarEventResponse[];
        const event = events.find(e => e.id === eventId);
        this.eventsRepo.removeOne(`${event.group}-${eventId}`);
      }),
      handleMessage({
        type: requestMessage('delete_event'),
      }),
    );
  };

  importCalendarTemplate = (data: CalendarImportRequest) => {
    return CalendarTemplateAPI.importCalendarTemplate(data).pipe(
      this.eventsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('import_calendar_template'),
      }),
    );
  };

  updateEventUserTask = (eventUserId: CalendarEventUserId, isAdmin: boolean) => {
    return CalendarEventAPI.updateEventUserTask(eventUserId, isAdmin).pipe(
      this.eventsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_event_user_task'),
      }),
    );
  };

}
